// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useSessionStore from '@/app/store/session';
import useCustomPayStore from '@/app/store/customPay';
// Components
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import DetailsGrid from '@/common-utils/components/renders/detailsGrid/DetailsGrid';
import ModalUpdateOrganization from '../modals/details/ModalUpdateOrganization';
import ModalUpdateSubDomain from '../modals/details/ModalUpdateSubDomain';
// Common
import Permissions from '@/app/permissions/Permissions';
import HiddenPermissions from '@/app/permissions/HiddenPermissions';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import ICONS from '@/common-utils/enums/icons';
import organizationFields from '../../fields/organizationFields';
import ORGANIZATION_TYPES from '../../enums/OrganizationTypes';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
// Style
import '@/common-utils/components/renders/detailsGrid/DetailsGrid.scss';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.organization
 * @param root0.setLaunchRefetch
 * @returns {JSX} OrganizationDetailsPanel
 */
function OrganizationDetailsPanel({ organization, setLaunchRefetch }) {

    // Constants
    const PP_PARTNERS_LIST = getObjectAttributeFromStringKey(organization, generateNestedFieldAccessor(organizationFields.PUSH_PAYMENT_PARTNERS));
    const PP_PARTNERS_NAMES = PP_PARTNERS_LIST && PP_PARTNERS_LIST.length > 0 &&
        PP_PARTNERS_LIST.map(item => item[organizationFields.PUSH_PAYMENT_PARTNERS.LEGAL_NAME.key]).join(', ');

    // Store
    const permissions = useSessionStore(s => s.session.permissions);
    const isCustomPay = useCustomPayStore((s) => s.isCustomPay);

    // State
    const [showModalUpdateOrganization, setShowModalUpdateOrganization] = useState();
    const [showModalUpdateType, setShowModalUpdateType] = useState();

    // Functions
    const isOfType = (...organizationTypes) => {
        return organization && organizationTypes.some(organizationType => organization[organizationFields.TYPE.key] === organizationType.name);
    };
    // Constants
    const urlProvider = isOfType(ORGANIZATION_TYPES.MERCHANT) && organization[organizationFields.PROVIDER_BRAND.key] ?
        getObjectAttributeFromStringKey(organization, generateNestedFieldAccessor(organizationFields.PROVIDER_BRAND.DOMAIN_NAME)) : null;

    // Functions
    const hasBillableReadPermissions = () => {
        return hasRequiredPermissions({
            permissions: permissions,
            requiredPermissions: [HiddenPermissions.BILLABILITY_READ.name]
        });
    };

    const hasBillingIdReadPermissions = () => {
        return hasRequiredPermissions({
            permissions: permissions,
            requiredPermissions: [HiddenPermissions.BILLING_ID_READ.name]
        });
    };

    const isOrganizationBillable = () => {
        const isBillable = getObjectAttributeFromStringKey(organization, organizationFields.BILLABLE.key);
        return organization ? (isBillable !== null && isBillable !== undefined) : false;
    };

    // Data to show
    const organizationFieldsToShow = {
        TYPE: organizationFields.TYPE,
        LEGAL_NAME: organizationFields.LEGAL_NAME,
        BUSINESS_NAME: organizationFields.BUSINESS_NAME,
        REGISTRATION_NUMBER: organizationFields.REGISTRATION_NUMBER,
        CLIENT_CODE: organizationFields.CLIENT_CODE,
        ...(hasBillableReadPermissions() && isOrganizationBillable()) && isOfType(ORGANIZATION_TYPES.WHITE_LABEL) && { BILLABLE: organizationFields.BILLABLE },
        ...(hasBillingIdReadPermissions() && { BILLING_ID_READ: organizationFields.BILLING_ID_READ }),
        COMMENT: organizationFields.COMMENT,
        CREATED_AT: organizationFields.CREATED_AT,
        UPDATED_AT: organizationFields.UPDATED_AT,
        ...(PP_PARTNERS_NAMES && { PUSH_PAYMENT_PARTNERS: organizationFields.PUSH_PAYMENT_PARTNERS.LEGAL_NAME })
    };

    const data = {
        ...organization,
        ...(PP_PARTNERS_NAMES && {
            [organizationFields.PUSH_PAYMENT_PARTNERS.key]: {
                [organizationFields.PUSH_PAYMENT_PARTNERS.LEGAL_NAME.key]: PP_PARTNERS_NAMES
            }
        })
    };

    return (
        <div >
            <PageActions
                actions={
                    [
                        {
                            icon: ICONS.ORGANIZATION,
                            entity: 'actions.page.update_organization',
                            namespace: nsOrganization,
                            onClick: () => setShowModalUpdateOrganization(true),
                            visible: true,
                            disabled: !hasRequiredPermissions({
                                permissions: permissions,
                                requiredPermissions: [Permissions.ORGANIZATION_UPDATE.name]
                            }),
                            tooltipForbidden: 'title.permissions.access_denied'
                        },
                        {
                            icon: ICONS.DOMAIN,
                            entity: 'actions.page.update_type',
                            namespace: nsOrganization,
                            onClick: () => setShowModalUpdateType(true),
                            visible: isOfType(ORGANIZATION_TYPES.MERCHANT) && !isCustomPay,
                            disabled: !hasRequiredPermissions({
                                permissions: permissions,
                                requiredPermissions: [Permissions.MB_ADMIN.name]
                            }),
                            tooltipForbidden: 'title.permissions.access_denied'
                        }
                    ]
                }
                retractable
            />
            <DetailsGrid
                data={data}
                fields={organizationFieldsToShow}
                permissions={permissions}
            />
            {showModalUpdateOrganization &&
                <ModalUpdateOrganization
                    isOpen={showModalUpdateOrganization}
                    setIsOpen={setShowModalUpdateOrganization}
                    organizationDetails={organization}
                    setLaunchRefetch={setLaunchRefetch}
                />
            }
            {showModalUpdateType &&
                <ModalUpdateSubDomain
                    isOpen={showModalUpdateType}
                    setIsOpen={setShowModalUpdateType}
                    organizationId={organization.uuid}
                    urlSubDomain={urlProvider}
                    setLaunchRefetch={setLaunchRefetch}
                    organizationType={ORGANIZATION_TYPES.MERCHANT.name}
                    modalTitle='modal.update_organization_type.title'
                />
            }
        </div>
    );
}

OrganizationDetailsPanel.propTypes = {
    organization: PropTypes.object,
    setLaunchRefetch: PropTypes.func
};

export default OrganizationDetailsPanel;